/**
 * Client Handler
 * 
 * Provides a high-level API for client applications integrating with Checkpoint.
 * Handles SSO callbacks, auth state management, URL parameter parsing, token
 * storage, login URL construction, and multi-account selection — so consuming
 * applications only need to deal with UI rendering.
 *
 * @package @appfactory/checkpoint-client
 * @version 1.0.0
 * 
 * @example
 * ```typescript
 * import CheckpointClient, { ClientHandler } from './checkpoint';
 * 
 * const checkpoint = new CheckpointClient({ baseUrl: '...' });
 * const handler = new ClientHandler(checkpoint, '/testing/client');
 * 
 * const result = await handler.handlePageLoad();
 * 
 * if (result.needsAccountSelection) {
 *   // Show accounts modal, then call handler.completeAccountSelection(index)
 * } else if (result.authState.isAuthenticated) {
 *   // Show authenticated UI
 * } else {
 *   // Show login UI
 * }
 * ```
 */

import CheckpointClient from './index';
import type { SecondaryUserAccountOption } from './secondary-users';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * User type enumeration
 * - 'normal': Primary IdP user (uses SSO ticket exchange)
 * - 'secondary': Tenant/secondary user (tokens passed directly)
 */
export type ClientUserType = 'normal' | 'secondary';

/**
 * Authentication state
 */
export interface AuthState {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Type of user (normal or secondary) */
  userType: ClientUserType;
  /** User profile data if authenticated */
  user: any | null;
  /** brain_memberships record if authenticated */
  member: Record<string, unknown> | null;
  /** Access token (for secondary users) */
  accessToken?: string | undefined;
  /** Refresh token (for secondary users) */
  refreshToken?: string | undefined;
  /** Token expiration timestamp (ms since epoch) */
  expiresAt?: number | undefined;
  /** Error message if auth failed */
  error?: string | undefined;
}

/**
 * SSO callback parameters extracted from URL
 */
export interface SsoCallbackParams {
  ssoTicket?: string | undefined;
  userType?: ClientUserType | undefined;
  user?: string | undefined;
  member?: string | undefined;
  accessToken?: string | undefined;
  refreshToken?: string | undefined;
  expiresIn?: string | undefined;
  appName?: string | undefined;
  superUserId?: string | undefined;
  multipleAccounts?: boolean | undefined;
  accounts?: string | undefined;
  selectionToken?: string | undefined;
}

/**
 * Account option for secondary user account selection
 */
export interface AccountOption {
  super_user_id: number;
  email: string;
  username: string;
  last_login?: string;
}

/**
 * Result of handlePageLoad()
 */
export interface PageLoadResult {
  /** The resolved authentication state */
  authState: AuthState;
  /** True if the user must pick from multiple accounts before auth completes */
  needsAccountSelection: boolean;
  /** Account options when needsAccountSelection is true */
  accounts?: AccountOption[];
  /** Selection token when needsAccountSelection is true */
  selectionToken?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** localStorage keys for secondary-user token persistence */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'checkpoint_secondary_access_token',
  REFRESH_TOKEN: 'checkpoint_secondary_refresh_token',
  TOKEN_EXPIRES: 'checkpoint_secondary_token_expires',
  USER_TYPE: 'checkpoint_user_type',
  USER_DATA: 'checkpoint_user_data',
  MEMBER_DATA: 'checkpoint_member_data',
} as const;

/** URL params to strip after processing a callback */
const SSO_URL_PARAMS = [
  'sso_ticket',
  'user',
  'member',
  'user_type',
  'app_name',
  'super_user_id',
  'access_token',
  'refresh_token',
  'expires_in',
  'multiple_accounts',
  'accounts',
  'selection_token',
] as const;

// =============================================================================
// CLIENT HANDLER
// =============================================================================

/**
 * ClientHandler
 * 
 * Encapsulates the full SSO callback lifecycle, auth state persistence,
 * and login URL construction so that consuming pages only need to render UI.
 */
export class ClientHandler {
  private checkpoint: CheckpointClient;
  private redirectUrl: string;
  private loginPath: string;

  /**
   * @param checkpoint  Initialised CheckpointClient instance
   * @param pagePath    Path of the current page (e.g. '/testing/client')
   * @param loginPath   Path to the login page (default: '/login')
   */
  constructor(
    checkpoint: CheckpointClient,
    pagePath: string,
    loginPath = '/login',
  ) {
    this.checkpoint = checkpoint;
    this.redirectUrl = window.location.origin + pagePath;
    this.loginPath = loginPath;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Handle everything that should happen on page load:
   * 1. If SSO callback params exist → process them
   * 2. Otherwise → check for stored / existing auth state
   * 
   * Returns a PageLoadResult so the caller can decide what to render.
   */
  async handlePageLoad(): Promise<PageLoadResult> {
    const params = this.parseUrlParams();

    // SSO callback with multiple accounts → needs account selection
    if (params.multipleAccounts && params.accounts && params.selectionToken) {
      try {
        const accounts: AccountOption[] = JSON.parse(
          decodeURIComponent(params.accounts),
        );
        const selectionToken = decodeURIComponent(params.selectionToken);

        return {
          authState: { isAuthenticated: false, userType: 'secondary', user: null, member: null },
          needsAccountSelection: true,
          accounts,
          selectionToken,
        };
      } catch (error) {
        console.error('[ClientHandler] Failed to parse account options:', error);
        this.cleanupUrl();
        return {
          authState: {
            isAuthenticated: false,
            userType: 'secondary',
            user: null,
            member: null,
            error: 'Failed to parse account options',
          },
          needsAccountSelection: false,
        };
      }
    }

    // SSO callback for secondary user
    if (params.userType === 'secondary' && params.accessToken) {
      let state = this.handleSecondaryCallback(params);
      // Secondary tokens arrive via the URL, so push them into the HTTP client
      // before any authenticated call (e.g. refreshMemberFromServer / listing
      // the app's users) is made.
      await this.persistSessionTokens(state);
      state = await this.refreshMemberFromServer(state);
      if (state.isAuthenticated) this.storeAuthState(state);
      this.cleanupUrl();
      return { authState: state, needsAccountSelection: false };
    }

    // SSO callback for normal user (ticket exchange)
    if (params.ssoTicket) {
      let state = await this.handleNormalCallback(params.ssoTicket, params.user, params.member);
      state = await this.refreshMemberFromServer(state);
      if (state.isAuthenticated) this.storeAuthState(state);
      this.cleanupUrl();
      return { authState: state, needsAccountSelection: false };
    }

    // No callback → check existing state
    const existing = await this.loadExistingAuthState();
    // Re-hydrate the HTTP client with a previously stored secondary session so
    // authenticated calls work on subsequent page loads too.
    await this.persistSessionTokens(existing);
    const authState = await this.refreshMemberFromServer(existing);
    return { authState, needsAccountSelection: false };
  }

  /**
   * Push a secondary-user session's tokens into the HTTP client so authenticated
   * API calls carry the bearer token. Normal (primary) users get their tokens
   * set during the SSO ticket exchange, so this only matters for secondary users
   * whose tokens arrive out-of-band (via the callback URL or local storage).
   */
  private async persistSessionTokens(state: AuthState): Promise<void> {
    if (!state.isAuthenticated || state.userType !== 'secondary' || !state.accessToken) {
      return;
    }
    const expiresIn = state.expiresAt
      ? Math.max(1, Math.round((state.expiresAt - Date.now()) / 1000))
      : 3600;
    try {
      await this.checkpoint.setSession({
        access_token: state.accessToken,
        expires_in: expiresIn,
        ...(state.refreshToken ? { refresh_token: state.refreshToken } : {}),
      });
    } catch (error) {
      console.warn('[ClientHandler] Failed to persist session tokens:', error);
    }
  }

  /**
   * Complete account selection after the user picks one.
   *
   * Calls the selectAccount API, stores the resulting tokens, and returns
   * the final AuthState.
   */
  async completeAccountSelection(
    selectionToken: string,
    account: AccountOption,
  ): Promise<AuthState> {
    try {
      const result = await this.checkpoint.secondaryUsers.selectAccount(
        selectionToken,
        account.super_user_id,
      );

      this.cleanupUrl();

      const state: AuthState = {
        isAuthenticated: true,
        userType: 'secondary',
        user: result.user,
        member: result.member ?? null,
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        expiresAt: Date.now() + result.expires_in * 1000,
      };

      const withMember = await this.refreshMemberFromServer(state);
      this.storeAuthState(withMember);
      return withMember;
    } catch (error) {
      this.cleanupUrl();
      return {
        isAuthenticated: false,
        userType: 'secondary',
        user: null,
        member: null,
        error: error instanceof Error ? error.message : 'Account selection failed',
      };
    }
  }

  /**
   * Build a login URL for normal or secondary users.
   */
  buildLoginUrl(
    userType: ClientUserType,
    appName?: string,
    superUserId?: number,
  ): string {

    // SSO domain is now guaranteed by the CheckpointClient configuration
    const base = this.checkpoint.ssoDomain;
    const url = new URL(this.loginPath, base);

    if (userType === 'secondary') {
      url.searchParams.set('user_type', 'secondary');
      url.searchParams.set('app_name', appName ?? this.checkpoint.appName);
      if (superUserId) url.searchParams.set('super_user_id', String(superUserId));
    } else {
      url.searchParams.set('target_domain', window.location.hostname);
      url.searchParams.set('app_name', appName ?? this.checkpoint.appName);
    }

    url.searchParams.set('redirect_url', this.redirectUrl);
    return url.toString();
  }

  /**
   * Log out: clear local state and call the SDK logout.
   */
  async logout(): Promise<void> {
    this.clearAuthState();
    this.checkpoint.authz.clearAuthContext();
    try {
      await this.checkpoint.auth.logout();
    } catch {
      // Ignore – user may already be logged out
    }
  }

  /**
   * Check whether the current page URL contains SSO callback params.
   */
  hasSsoCallbackParams(): boolean {
    const p = this.parseUrlParams();
    return !!(p.ssoTicket || p.userType === 'secondary' || p.multipleAccounts);
  }

  /**
   * Expose the checkpoint instance for advanced use.
   */
  getCheckpoint(): CheckpointClient {
    return this.checkpoint;
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  private parseUrlParams(): SsoCallbackParams {
    const u = new URLSearchParams(window.location.search);
    return {
      ssoTicket: u.get('sso_ticket') || undefined,
      userType: (u.get('user_type') as ClientUserType) || undefined,
      user: u.get('user') || undefined,
      member: u.get('member') || undefined,
      accessToken: u.get('access_token') || undefined,
      refreshToken: u.get('refresh_token') || undefined,
      expiresIn: u.get('expires_in') || undefined,
      appName: u.get('app_name') || undefined,
      superUserId: u.get('super_user_id') || undefined,
      multipleAccounts: u.get('multiple_accounts') === 'true',
      accounts: u.get('accounts') || undefined,
      selectionToken: u.get('selection_token') || undefined,
    };
  }

  private cleanupUrl(): void {
    const url = new URL(window.location.href);
    SSO_URL_PARAMS.forEach((p) => url.searchParams.delete(p));
    window.history.replaceState({}, document.title, url.toString());
  }

  private async refreshMemberFromServer(state: AuthState): Promise<AuthState> {
    // Every AuthState produced by this handler passes through here before
    // being returned, so this is the one place that needs to keep authz's
    // secondary-user role/permission context in sync with the session.
    if (state.isAuthenticated) {
      this.checkpoint.authz.setAuthContext(state.userType, this.checkpoint.appName, state.user);
    }

    if (!state.isAuthenticated || state.member) {
      return state;
    }

    try {
      const token =
        state.accessToken ??
        localStorage.getItem('checkpoint_access_token') ??
        undefined;

      if (!token) {
        return state;
      }

      if (state.userType === 'secondary') {
        const result = await this.checkpoint.secondaryUsers.validateToken(token);
        if (result.valid && result.member) {
          state.member = result.member;
          this.storeAuthState(state);
        }
      } else {
        const tokenInfo = await this.checkpoint.auth.verifyToken(token);
        if (tokenInfo.member) {
          state.member = tokenInfo.member;
          this.storeAuthState(state);
        }
      }
    } catch (error) {
      console.warn('[ClientHandler] Unable to refresh member payload:', error);
    }

    return state;
  }

  private parseMemberParam(memberParam?: string): Record<string, unknown> | null {
    if (!memberParam) return null;
    try {
      return JSON.parse(decodeURIComponent(memberParam));
    } catch {
      return null;
    }
  }

  private handleSecondaryCallback(params: SsoCallbackParams): AuthState {
    try {
      let user = null;
      if (params.user) user = JSON.parse(decodeURIComponent(params.user));

      let expiresAt: number | undefined;
      if (params.expiresIn) expiresAt = Date.now() + parseInt(params.expiresIn, 10) * 1000;

      return {
        isAuthenticated: true,
        userType: 'secondary',
        user,
        member: this.parseMemberParam(params.member),
        accessToken: params.accessToken ? decodeURIComponent(params.accessToken) : undefined,
        refreshToken: params.refreshToken ? decodeURIComponent(params.refreshToken) : undefined,
        expiresAt,
      };
    } catch (error) {
      return {
        isAuthenticated: false,
        userType: 'secondary',
        user: null,
        member: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  private async handleNormalCallback(
    ssoTicket: string,
    userParam?: string,
    memberParam?: string,
  ): Promise<AuthState> {
    let urlUser: any = null;
    if (userParam) {
      try { urlUser = JSON.parse(decodeURIComponent(userParam)); } catch { /* ignore */ }
    }
    const urlMember = this.parseMemberParam(memberParam);

    try {
      const result = await this.checkpoint.auth.ssoExchangeToken(
        ssoTicket,
        window.location.hostname,
      );

      const expiresAt = result.expires_in
        ? Date.now() + result.expires_in * 1000
        : undefined;

      return {
        isAuthenticated: true,
        userType: 'normal',
        user: result.user || urlUser,
        member: result.member ?? urlMember,
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        expiresAt,
      };
    } catch {
      if (urlUser) {
        return {
          isAuthenticated: true,
          userType: 'normal',
          user: urlUser,
          member: urlMember,
        };
      }
      return {
        isAuthenticated: false,
        userType: 'normal',
        user: null,
        member: null,
        error: 'SSO ticket exchange failed',
      };
    }
  }

  private async loadExistingAuthState(): Promise<AuthState> {
    const stored = this.loadStoredAuthState();
    if (stored) {
      return stored;
    }

    try {
      const isAuth = await this.checkpoint.isAuthenticated();
      if (isAuth) {
        const user = await this.checkpoint.auth.getCurrentUser();
        const member = await this.checkpoint.auth.getCurrentMember();

        return {
          isAuthenticated: true,
          userType: 'normal',
          user,
          member,
        };
      }
    } catch { /* not authenticated */ }

    return { isAuthenticated: false, userType: 'normal', user: null, member: null };
  }

  private storeAuthState(state: AuthState): void {
    localStorage.setItem(STORAGE_KEYS.USER_TYPE, state.userType);
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(state.user));
    localStorage.setItem(
      STORAGE_KEYS.MEMBER_DATA,
      JSON.stringify(state.member ?? null),
    );
    if (state.accessToken) localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, state.accessToken);
    if (state.refreshToken) localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, state.refreshToken);
    if (state.expiresAt) localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES, String(state.expiresAt));
  }

  private loadStoredAuthState(): AuthState | null {
    const userType = localStorage.getItem(STORAGE_KEYS.USER_TYPE) as ClientUserType | null;
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    const memberData = localStorage.getItem(STORAGE_KEYS.MEMBER_DATA);
    if (!userType || !userData) return null;

    const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES);
    if (expiresAt && parseInt(expiresAt, 10) < Date.now()) {
      this.clearAuthState();
      return null;
    }

    return {
      isAuthenticated: true,
      userType,
      user: JSON.parse(userData),
      member: memberData ? JSON.parse(memberData) : null,
      accessToken: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || undefined,
      refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || undefined,
      expiresAt: expiresAt ? parseInt(expiresAt, 10) : undefined,
    };
  }

  private clearAuthState(): void {
    Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
  }
}
